import {
  PayPerLeadSubscription,
  StateIdType,
  SubscriptionItemServerModel,
  SubscriptionServerModel,
  SubscriptionStateItem,
} from "@containers/PayPerLead/interface";

const toServerModel = (subscription: PayPerLeadSubscription, accountId?: number): SubscriptionServerModel => {
  const serverModelItems: SubscriptionItemServerModel[] = [];

  for (const stateItem of subscription.items) {
    if (stateItem.state_id === StateIdType.Nationwide) {
      serverModelItems.push({
        leads_total: stateItem.leads_total as number,
        all_counties: false,
        all_states: true,
        counties: [],
      });
    } else if (stateItem.all_counties) {
      serverModelItems.push({
        state_id: stateItem.state_id as number,
        leads_total: stateItem.leads_total as number,
        all_counties: true,
        counties: [],
      });
    } else {
      if (stateItem.force_total_per_county) {
        for (const countyItem of stateItem.counties) {
          serverModelItems.push({
            state_id: stateItem.state_id as number,
            leads_total: countyItem.total_leads as number,
            all_counties: false,
            counties: [
              {
                id: countyItem.county_id,
                name: countyItem.name as string,
              },
            ],
          });
        }
      } else {
        serverModelItems.push({
          state_id: stateItem.state_id as number,
          leads_total: stateItem.leads_total as number,
          all_counties: false,
          counties: stateItem.counties.map((countyItem) => ({
            id: countyItem.county_id,
            name: countyItem.name as string,
          })),
        });
      }
    }
  }

  return {
    id: subscription.id,
    account_id: accountId,
    items: serverModelItems,
  };
};

const fromServerModel = (subscription: SubscriptionServerModel): PayPerLeadSubscription => {
  const groupedByStateId: Record<number | string, SubscriptionStateItem> = {};
  let totalPrice = 0;

  for (const item of subscription.items ?? []) {
    if (item.price) {
      totalPrice += +item.price;
    }

    if (item.all_states) {
      groupedByStateId[StateIdType.Nationwide] = {
        state_id: StateIdType.Nationwide,
        leads_total: item.leads_total,
        price: item.price,
        unassigned_leads: item.unassigned_leads,
        all_counties: true,
        force_total_per_county: false,
        counties: [],
        paid_amount: item.paid_amount,
      };
    }

    if (item.state_id) {
      if (item.all_counties) {
        groupedByStateId[item.state_id] = {
          state_id: item.state_id as number,
          leads_total: item.leads_total,
          price: item.price,
          unassigned_leads: item.unassigned_leads,
          all_counties: true,
          force_total_per_county: false,
          counties: [],
          paid_amount: item.paid_amount,
        };
      } else {
        if (groupedByStateId[item.state_id]) {
          const stateItem = groupedByStateId[item.state_id];

          const newCounties = item.counties.map(({ id, name }) => ({
            county_id: id,
            total_leads: item.leads_total,
            state_id: item.state_id as number,
            name,
          }));

          stateItem.leads_total = (stateItem.leads_total as number) + item.leads_total;
          stateItem.counties.push(...newCounties);
          // TODO: price & leads should be calculated based on counties
          stateItem.price = item.price;
        } else {
          groupedByStateId[item.state_id] = {
            force_total_per_county: item.counties.length === 1,
            state_id: item.state_id as number,
            leads_total: item.leads_total,
            all_counties: false,
            // TODO: price & leads should be calculated based on counties
            price: item.price,
            counties: item.counties.map(({ id, name }) => ({
              county_id: id,
              total_leads: item.leads_total,
              state_id: item.state_id as number,
              name,
            })),
            paid_amount: item.paid_amount,
          };
        }
      }
    }
  }

  return {
    items: Object.values(groupedByStateId),
    price: totalPrice,
    id: subscription.id,
    is_active: subscription.is_active,
    is_paused: subscription.is_paused,
    renew: subscription.renew,
    draft: subscription.draft,
    activated_at: subscription.activated_at,
    created_at: subscription.created_at,
    updated_at: subscription.updated_at,
    start_date: subscription.start_date,
    end_date: subscription.end_date,
    coupon: subscription.coupon ?? null,
    lock: subscription.lock,
    remaining_balance: subscription?.remaining_balance,
    cancelled_at: subscription.cancelled_at,
    next_billing_at: subscription.next_billing_at,
    cbStatus: subscription.status,
    discounts: subscription.discounts,
  };
};

export const SubscriptionConverter = { fromServerModel, toServerModel };
